
import { supabase } from '../integrations/supabase/client';
import { toast } from 'sonner';

// Production base URL constant
const PRODUCTION_BASE_URL = 'https://www.petius.io';

/**
 * Detects if we're running in a mobile app (Capacitor) environment
 * @returns boolean indicating if running in Capacitor
 */
const isCapacitorApp = (): boolean => {
  return !!(window as any).Capacitor;
};

/**
 * Detects if we're in development environment
 * @returns boolean indicating if in development
 */
const isDevelopmentEnvironment = (): boolean => {
  // Check Vite environment variable first
  if (import.meta.env.MODE === 'development') {
    return true;
  }
  
  // Fallback to hostname check for web browsers
  return window.location.hostname === 'localhost' || 
         window.location.hostname.includes('127.0.0.1') ||
         window.location.hostname.includes('.local');
};

/**
 * Gets a shareable link for a memory post
 * @param postId The ID of the memory post
 * @returns A shareable URL for the post
 */
export const getShareableLink = (postId: string): string => {
  // Always use production URL for mobile apps (Capacitor)
  if (isCapacitorApp()) {
    console.log('🔗 Capacitor detected - using production URL for sharing');
    return `${PRODUCTION_BASE_URL}/post/${postId}`;
  }
  
  // For web browsers, check development vs production
  const isDev = isDevelopmentEnvironment();
  const baseUrl = isDev ? window.location.origin : PRODUCTION_BASE_URL;
  
  console.log('🔗 Generating shareable link:', {
    isCapacitor: false,
    isDevelopment: isDev,
    baseUrl,
    finalUrl: `${baseUrl}/post/${postId}`
  });
  
  return `${baseUrl}/post/${postId}`;
};

/**
 * Copies a post link to the clipboard
 * @param postId The ID of the memory post to share
 */
export const copyPostLinkToClipboard = async (postId: string): Promise<void> => {
  try {
    const link = getShareableLink(postId);
    await navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard!');
  } catch (error) {
    console.error('Error copying link to clipboard:', error);
    toast.error('Failed to copy link to clipboard');
    // Fallback for browsers that don't support clipboard API
    promptCopyLink(getShareableLink(postId));
  }
};

/**
 * Fallback method to prompt user to copy link manually
 * @param link The link to copy
 */
const promptCopyLink = (link: string): void => {
  // Create a temporary input element
  const tempInput = document.createElement('input');
  tempInput.value = link;
  document.body.appendChild(tempInput);
  
  // Select the text
  tempInput.select();
  tempInput.setSelectionRange(0, 99999); // For mobile devices
  
  // Alert the user to copy manually
  alert(`Please copy this link manually: ${link}`);
  
  // Remove the temporary element
  document.body.removeChild(tempInput);
};

/**
 * Checks if a post is publicly accessible
 * @param postId The ID of the memory post to check
 * @returns A boolean indicating if the post is public
 */
export const isPostPublic = async (postId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('memory_posts')
      .select('privacy_setting')
      .eq('id', postId)
      .single();

    if (error) {
      console.error('Error checking post privacy:', error);
      return false;
    }

    return data?.privacy_setting === 'public';
  } catch (error) {
    console.error('Error checking post privacy:', error);
    return false;
  }
};

/**
 * Creates a personalized share message with pet name
 * @param petName Optional pet name
 * @returns Personalized message
 */
const getPersonalizedMessage = (petName?: string): string => {
  return petName 
    ? `¡Conoce esta nueva foto de ${petName} en Petius!`
    : `¡Mira esta publicación en Petius!`;
};

/**
 * Share post to Facebook
 * @param postId The ID of the memory post to share
 * @param petName Optional pet name for personalized message
 */
export const shareToFacebook = (postId: string, petName?: string): void => {
  try {
    const url = getShareableLink(postId);
    const message = getPersonalizedMessage(petName);
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(message)}`;
    
    // Open a popup window for sharing
    window.open(shareUrl, '_blank', 'width=600,height=400');
    toast.success('Sharing to Facebook...');
  } catch (error) {
    console.error('Error sharing to Facebook:', error);
    toast.error('Failed to share to Facebook');
  }
};

/**
 * Share post to Twitter/X
 * @param postId The ID of the memory post to share
 * @param petName Optional pet name for personalized message
 */
export const shareToTwitter = (postId: string, petName?: string): void => {
  try {
    const url = getShareableLink(postId);
    const message = getPersonalizedMessage(petName);
    const text = `${message} ${url}`;
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    
    // Open a popup window for sharing
    window.open(shareUrl, '_blank', 'width=600,height=400');
    toast.success('Sharing to X...');
  } catch (error) {
    console.error('Error sharing to X:', error);
    toast.error('Failed to share to X');
  }
};

/**
 * Share post via WhatsApp
 * @param postId The ID of the memory post to share
 * @param petName Optional pet name for personalized message
 */
export const shareToWhatsApp = (postId: string, petName?: string): void => {
  try {
    const url = getShareableLink(postId);
    const message = getPersonalizedMessage(petName);
    const text = `${message} ${url}`;
    const shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
    
    // Open a new window for WhatsApp sharing
    window.open(shareUrl, '_blank');
    toast.success('Sharing to WhatsApp...');
  } catch (error) {
    console.error('Error sharing to WhatsApp:', error);
    toast.error('Failed to share to WhatsApp');
  }
};

/**
 * Share post to WeChat (copies link to clipboard)
 * @param postId The ID of the memory post to share
 * @param petName Optional pet name for personalized message
 */
export const shareToWeChat = (postId: string, petName?: string): void => {
  try {
    const url = getShareableLink(postId);
    const message = getPersonalizedMessage(petName);
    const fullText = `${message} ${url}`;
    navigator.clipboard.writeText(fullText).then(() => {
      toast.success('✅ Link copied! Open WeChat and paste it in a chat or moment.', {
        duration: 4000,
        position: 'top-center',
      });
    }).catch(() => {
      toast.info('Please copy this link and share it on WeChat: ' + fullText, {
        duration: 4000,
      });
    });
  } catch (error) {
    console.error('Error preparing WeChat share:', error);
    toast.error('Failed to prepare WeChat share');
  }
};

/**
 * Share post via Facebook Messenger
 * @param postId The ID of the memory post to share
 * @param petName Optional pet name for personalized message
 */
export const shareToMessenger = (postId: string, petName?: string): void => {
  try {
    const url = getShareableLink(postId);
    const message = getPersonalizedMessage(petName);
    const text = `${message} ${url}`;
    const shareUrl = `https://www.messenger.com/t/?text=${encodeURIComponent(text)}`;
    
    // Open a new window for Messenger sharing
    window.open(shareUrl, '_blank');
    toast.success('Sharing to Facebook Messenger...');
  } catch (error) {
    console.error('Error sharing to Messenger:', error);
    toast.error('Failed to share to Facebook Messenger');
  }
};

/**
 * Share post via Telegram
 * @param postId The ID of the memory post to share
 * @param petName Optional pet name for personalized message
 */
export const shareToTelegram = (postId: string, petName?: string): void => {
  try {
    const url = getShareableLink(postId);
    const message = getPersonalizedMessage(petName);
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(message)}`;
    
    // Open a new window for Telegram sharing
    window.open(shareUrl, '_blank');
    toast.success('Sharing to Telegram...');
  } catch (error) {
    console.error('Error sharing to Telegram:', error);
    toast.error('Failed to share to Telegram');
  }
};

/**
 * Share post to Discord (copies link to clipboard)
 * @param postId The ID of the memory post to share
 * @param petName Optional pet name for personalized message
 */
export const shareToDiscord = (postId: string, petName?: string): void => {
  try {
    const url = getShareableLink(postId);
    const message = getPersonalizedMessage(petName);
    const fullText = `${message} ${url}`;
    navigator.clipboard.writeText(fullText).then(() => {
      toast.success('✅ Link copied! Open Discord and paste it in a channel or direct message.', {
        duration: 4000,
        position: 'top-center',
      });
    }).catch(() => {
      toast.info('Please copy this link and share it on Discord: ' + fullText, {
        duration: 4000,
      });
    });
  } catch (error) {
    console.error('Error preparing Discord share:', error);
    toast.error('Failed to prepare Discord share');
  }
};

/**
 * Share post via Email
 * @param postId The ID of the memory post to share
 * @param petName Optional pet name for the subject and body
 */
export const shareViaEmail = (postId: string, petName?: string): void => {
  try {
    const url = getShareableLink(postId);
    const message = getPersonalizedMessage(petName);
    const subject = petName ? `Check out ${petName}'s photo on Petius!` : 'Check out this pet photo on Petius!';
    const body = `${message}\n\n${url}`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open default email client
    window.location.href = mailtoUrl;
    toast.success('Opening email client...');
  } catch (error) {
    console.error('Error sharing via email:', error);
    toast.error('Failed to open email client');
  }
};

/**
 * Opens a social media sharing dialog
 * @param postId The ID of the memory post to share 
 * @param caption Optional caption or text to include
 * @param petName Optional pet name to include
 */
export const openShareDialog = (postId: string, caption?: string, petName?: string): void => {
  // If Web Share API is available, use it (mobile-friendly)
  if (navigator.share) {
    const shareData = {
      title: petName ? `${petName}'s Memory` : 'Petius Memory',
      text: caption || 'Check out this pet memory!',
      url: getShareableLink(postId),
    };
    
    navigator.share(shareData)
      .then(() => toast.success('Shared successfully!'))
      .catch((error) => {
        console.error('Error sharing:', error);
        // Fallback to clipboard if sharing fails
        copyPostLinkToClipboard(postId);
      });
  } else {
    // Fallback to clipboard on platforms without Web Share API
    copyPostLinkToClipboard(postId);
  }
};
